import { useState } from 'react';
import { Check, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScoreBySummary } from './score-by-summary';
import { ScoreWinnerOnly } from './score-winner-only';
import { submitResult } from '@/data/submit-result';
import { undoMatchResult } from '@/data/undo-result';
import { toast } from '@/components/ui/sonner';
import type { Match, MatchFormat, ScoreMode } from '@/data/types';

interface Props {
  tournamentId: string;
  match: Match | null;
  matchFormat: MatchFormat;
  scoreMode: ScoreMode;
  onClose: () => void;
}

export function MatchInputDialog({
  tournamentId,
  match,
  matchFormat,
  scoreMode,
  onClose,
}: Props) {
  const [submitting, setSubmitting] = useState(false);

  async function handleSummary(winnerId: string, aSets: number, bSets: number) {
    if (!match) return;
    setSubmitting(true);
    try {
      await submitResult({
        tournamentId,
        matchId: match.id,
        score: { sets: [], winnerId, summary: `${aSets}:${bSets}` },
      });
      toast.success('Сохранено');
      onClose();
    } catch (err) {
      toast.error('Не получилось', { description: String((err as Error).message) });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleWinnerOnly(winnerId: string) {
    if (!match) return;
    setSubmitting(true);
    try {
      await submitResult({
        tournamentId,
        matchId: match.id,
        score: { sets: [], winnerId, summary: 'win' },
      });
      toast.success('Сохранено');
      onClose();
    } catch (err) {
      toast.error('Не получилось', { description: String((err as Error).message) });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUndo() {
    if (!match) return;
    if (!confirm('Откатить результат? Победитель будет убран из следующего матча.')) return;
    setSubmitting(true);
    try {
      await undoMatchResult(tournamentId, match.id);
      toast.success('Результат откачен');
      onClose();
    } catch (err) {
      toast.error('Не получилось', { description: String((err as Error).message) });
    } finally {
      setSubmitting(false);
    }
  }

  const isDone = match?.status === 'COMPLETED';
  const winnerIsA = match?.score?.winnerId === match?.playerAId;
  const winnerName = winnerIsA ? match?.playerAName : match?.playerBName;

  return (
    <Dialog open={!!match} onOpenChange={(open) => !submitting && !open && onClose()}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        <DialogHeader style={{ padding: '18px 22px 12px', borderBottom: '1px solid var(--line)' }}>
          <DialogTitle>
            <span className="row gap-3" style={{ alignItems: 'center' }}>
              <span>
                {match?.playerAName} — {match?.playerBName}
              </span>
              {isDone && (
                <span className="badge badge-done">
                  <Check size={11} /> Сыгран
                </span>
              )}
            </span>
          </DialogTitle>
          <div className="t-small t-mute" style={{ marginTop: 4 }}>
            {matchFormat} · {scoreMode === 'SETS' ? 'Счёт по сетам' : 'Только победитель'}
          </div>
        </DialogHeader>

        {/* Содержимое + оверлей загрузки поверх него на время записи. */}
        <div style={{ position: 'relative', padding: '20px 22px' }}>
          {match && isDone && (
            <div>
              <div
                className="row gap-3"
                style={{
                  padding: 14,
                  background: 'var(--bg-sunken)',
                  borderRadius: 'var(--r-3)',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    className="row gap-2"
                    style={{ marginBottom: 8, fontWeight: winnerIsA ? 600 : 400 }}
                  >
                    {winnerIsA && (
                      <span className="check">
                        <Check size={12} strokeWidth={3} />
                      </span>
                    )}
                    <span>{match.playerAName}</span>
                  </div>
                  <div
                    className="row gap-2"
                    style={{
                      fontWeight: !winnerIsA ? 600 : 400,
                      color: !winnerIsA ? 'var(--fg)' : 'var(--fg-muted)',
                    }}
                  >
                    {!winnerIsA && (
                      <span className="check">
                        <Check size={12} strokeWidth={3} />
                      </span>
                    )}
                    <span>{match.playerBName}</span>
                  </div>
                </div>
                <div
                  className="t-mono"
                  style={{ fontSize: 28, fontWeight: 600 }}
                >
                  {match.score?.summary === 'win' ? '✓' : match.score?.summary}
                </div>
              </div>
              <div className="t-xs t-mute" style={{ marginTop: 12 }}>
                Победитель: <strong style={{ color: 'var(--fg)' }}>{winnerName}</strong>
              </div>
            </div>
          )}

          {match && !isDone && (
            <>
              {scoreMode === 'WINNER_ONLY' ? (
                <ScoreWinnerOnly match={match} onPick={handleWinnerOnly} disabled={submitting} />
              ) : (
                <ScoreBySummary
                  match={match}
                  format={matchFormat}
                  onPick={handleSummary}
                  disabled={submitting}
                />
              )}
            </>
          )}

          {/* Оверлей сохранения */}
          {submitting && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(2px)',
                display: 'grid',
                placeItems: 'center',
                zIndex: 10,
              }}
            >
              <div
                className="row gap-3"
                style={{
                  background: 'var(--bg-elev)',
                  padding: '10px 16px',
                  borderRadius: 'var(--r-2)',
                  boxShadow: 'var(--sh-3)',
                  border: '1px solid var(--line)',
                  fontSize: 14,
                }}
              >
                <span className="spinner" />
                Сохранение...
              </div>
            </div>
          )}
        </div>

        {isDone && (
          <div
            style={{
              padding: '14px 22px',
              borderTop: '1px solid var(--line)',
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              background: 'var(--bg-sunken)',
            }}
          >
            <button className="btn" onClick={onClose} disabled={submitting}>
              Закрыть
            </button>
            <button className="btn btn-danger" onClick={handleUndo} disabled={submitting}>
              <RefreshCw size={14} /> Откатить результат
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
